import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Space,
  Tooltip,
  message,
  Spin,
  Tabs,
  Input,
  Select,
  List,
  Avatar,
  Divider,
  Modal,
} from 'antd';
import {
  SaveOutlined,
  UndoOutlined,
  RedoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  DeleteOutlined,
  PlusOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { PlanogramEditorProps, Shelf, Product, Planogram } from '../../../types/storeGroup';
import StoreGroupService from '../../../services/storeGroupService';
import HQDisplayGroupService from '../../../services/hqDisplayGroupService';
import CategoryService from '../../../services/categoryService';
import { itemService } from '../../item-store-relationship';

const { TabPane } = Tabs;
const { Option } = Select;

const PlanogramEditor: React.FC<PlanogramEditorProps> = ({
  planogram: initialPlanogram,
  onSave,
  onCancel,
  representativeStoreData,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [planogram, setPlanogram] = useState<Planogram>(initialPlanogram);
  const [shelves, setShelves] = useState<Shelf[]>(initialPlanogram.layout.shelves);
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categories, setCategories] = useState<any[]>([]);
  const [zoom, setZoom] = useState<number>(1);
  const [selectedShelf, setSelectedShelf] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  const storeGroupService = StoreGroupService;
  const hqDisplayGroupService = HQDisplayGroupService;

  useEffect(() => {
    fetchCategories();
    // 初始化历史记录
    setHistory([{
      shelves: [...initialPlanogram.layout.shelves],
    }]);
    setHistoryIndex(0);
  }, []);

  const fetchCategories = async () => {
    try {
      const categoriesData = await CategoryService.listCategories();
      setCategories(categoriesData.data || []);
    } catch (error) {
      message.error(t('planogramEditor.fetchCategoriesFailed'));
      console.error('Failed to fetch category list:', error);
    }
  };

  const fetchProducts = async (keyword: string = '', categoryId: string = '') => {
    try {
      const productsData = await itemService.listItems({ searchStr: keyword});
      setAvailableProducts(productsData.data || []);
    } catch (error) {
      message.error(t('planogramEditor.fetchProductsFailed'));
      console.error('Failed to fetch product list:', error);
    }
  };

  const handleSearch = () => {
    fetchProducts(searchKeyword, selectedCategory);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    fetchProducts(searchKeyword, value);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedPlanogram = {
        ...planogram,
        layout: {
          ...planogram.layout,
          shelves,
        },
      };
      await onSave(updatedPlanogram);
      message.success(t('planogramEditor.saveSuccess'));
    } catch (error) {
      message.error(t('planogramEditor.saveFailed'));
      console.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const addToHistory = (newShelves: Shelf[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ shelves: [...newShelves] });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setShelves([...history[newIndex].shelves]);
      setHistoryIndex(newIndex);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setShelves([...history[newIndex].shelves]);
      setHistoryIndex(newIndex);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.3));
  };

  const handleSelectShelf = (shelfId: string) => {
    setSelectedShelf(shelfId);
    setSelectedProduct(null);
  };

  const handleSelectProduct = (productId: string) => {
    setSelectedProduct(productId);
  };

  const handleAddShelf = () => {
    const newShelf: Shelf = {
      id: `shelf_${Date.now()}`,
      position: shelves.length + 1,
      height: 100,
      width: 200,
      remainingSpace: 200,
      products: [],
    };
    
    const newShelves = [...shelves, newShelf];
    setShelves(newShelves);
    addToHistory(newShelves);
  };

  const handleDeleteShelf = (shelfId: string) => {
    Modal.confirm({
      title: t('common.confirmDelete'),
      content: t('planogramEditor.confirmDeleteShelf'),
      okText: t('common.confirm'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: () => {
        const newShelves = shelves.filter(shelf => shelf.id !== shelfId);
        setShelves(newShelves);
        setSelectedShelf(null);
        addToHistory(newShelves);
      },
    });
  };

  const handleAddProduct = (product: any, shelfId: string) => {
    if (!shelfId) return;
    
    const shelf = shelves.find(s => s.id === shelfId);
    if (!shelf) return;
    
    const newProduct: Product = {
      id: `product_${Date.now()}`,
      code: product.code,
      name: product.name,
      width: product.width || 10,
      position: shelf.products.length + 1,
      facings: 1,
      inventory: representativeStoreData?.inventory[product.code] || 0,
      dailySales: representativeStoreData?.dailySales[product.code] || 0,
      suggestedFacings: 1,
      affectedStores: 0,
    };
    
    const newShelves = shelves.map(s => 
      s.id === shelfId 
        ? { ...s, products: [...s.products, newProduct] }
        : s
    );
    setShelves(newShelves);
    addToHistory(newShelves);
  };

  const handleDeleteProduct = (productId: string) => {
    const newShelves = shelves.map(shelf => ({
      ...shelf,
      products: shelf.products.filter(product => product.id !== productId)
    }));
    setShelves(newShelves);
    setSelectedProduct(null);
    addToHistory(newShelves);
  };

  return (
    <div className="planogram-editor">
      <div className="editor-toolbar" style={{ marginBottom: 16 }}>
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            {t('common.save')}
          </Button>
          <Button
            icon={<UndoOutlined />}
            onClick={handleUndo}
            disabled={historyIndex <= 0}
          >
            {t('planogramEditor.undo')}
          </Button>
          <Button
            icon={<RedoOutlined />}
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
          >
            {t('planogramEditor.redo')}
          </Button>
          <Divider type="vertical" />
          <Button icon={<ZoomInOutlined />} onClick={handleZoomIn}>
            {t('planogramEditor.zoomIn')}
          </Button>
          <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut}>
            {t('planogramEditor.zoomOut')}
          </Button>
          <Button icon={<FullscreenOutlined />}>
            {t('planogramEditor.fitScreen')}
          </Button>
        </Space>
      </div>

      <div className="editor-content" style={{ display: 'flex' }}>
        <div
          className="canvas-container"
          style={{
            flex: 1,
            border: '1px solid #d9d9d9',
            borderRadius: '2px',
            position: 'relative',
            overflow: 'auto',
            height: '600px',
          }}
        >
          <div
            ref={canvasRef}
            className="canvas"
            style={{
              width: `${planogram?.layout?.width || 800}px`,
              height: `${planogram?.layout?.height || 600}px`,
              transform: `scale(${zoom})`,
              transformOrigin: '0 0',
              position: 'relative',
              backgroundColor: '#f0f2f5',
            }}
          >
            {shelves.map((shelf, shelfIndex) => (
              <div
                key={shelf.id}
                className={`shelf ${selectedShelf === shelf.id ? 'selected' : ''}`}
                style={{
                  position: 'absolute',
                  left: '50px',
                  top: `${50 + shelfIndex * 120}px`,
                  width: `${shelf.width}px`,
                  height: `${shelf.height}px`,
                  backgroundColor: '#ffffff',
                  border: selectedShelf === shelf.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  cursor: 'pointer',
                  zIndex: selectedShelf === shelf.id ? 100 : 1,
                }}
                onClick={() => handleSelectShelf(shelf.id)}
              >
                <div
                  className="shelf-header"
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#f0f0f0',
                    borderBottom: '1px solid #d9d9d9',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  {t('planogramEditor.shelfName', { number: shelf.position })}
                  {selectedShelf === shelf.id && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      style={{ float: 'right', marginTop: '-2px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteShelf(shelf.id);
                      }}
                    />
                  )}
                </div>
                <div style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {shelf.products.map((product, productIndex) => (
                    <div
                      key={product.id}
                      className={`product ${selectedProduct === product.id ? 'selected' : ''}`}
                      style={{
                        width: '40px',
                        height: '60px',
                        backgroundColor: '#f9f9f9',
                        border: selectedProduct === product.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                        borderRadius: '2px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '2px',
                        fontSize: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectProduct(product.id);
                      }}
                    >
                      <div style={{ 
                        fontSize: '6px', 
                        textAlign: 'center', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        width: '100%' 
                      }}>
                        {product.name}
                      </div>
                      <div style={{ fontSize: '6px', color: '#666' }}>
                        {product.facings}x
                      </div>
                      {selectedProduct === product.id && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          style={{
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            padding: '0',
                            minWidth: 'auto',
                            fontSize: '8px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProduct(product.id);
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="sidebar"
          style={{
            width: '300px',
            marginLeft: '16px',
          }}
        >
          <Tabs defaultActiveKey="shelves">
            <TabPane
              tab={
                <span>
                  <AppstoreOutlined />
                  {t('planogramEditor.shelves')}
                </span>
              }
              key="shelves"
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddShelf}
                style={{ marginBottom: '16px' }}
              >
                {t('planogramEditor.addShelf')}
              </Button>
              <List
                size="small"
                bordered
                dataSource={shelves}
                renderItem={shelf => (
                  <List.Item
                    className={selectedShelf === shelf.id ? 'selected-item' : ''}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: selectedShelf === shelf.id ? '#e6f7ff' : 'transparent',
                    }}
                    onClick={() => handleSelectShelf(shelf.id)}
                  >
                    <List.Item.Meta
                      title={t('planogramEditor.shelfName', { number: shelf.position })}
                      description={`${shelf.width} x ${shelf.height} | ${shelf.products.length} ${t('planogramEditor.products')}`}
                    />
                  </List.Item>
                )}
              />
            </TabPane>
            <TabPane
              tab={
                <span>
                  <ShoppingOutlined />
                  {t('planogramEditor.products')}
                </span>
              }
              key="products"
            >
              <div style={{ marginBottom: '16px' }}>
                <Input
                  placeholder={t('planogramEditor.searchProducts')}
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onPressEnter={handleSearch}
                  suffix={
                    <Button
                      type="text"
                      icon={<SearchOutlined />}
                      onClick={handleSearch}
                    />
                  }
                  style={{ marginBottom: '8px' }}
                />
                <Select
                  placeholder={t('planogramEditor.selectCategory')}
                  style={{ width: '100%' }}
                  allowClear
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                >
                  {categories.map(category => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </div>
              <List
                size="small"
                bordered
                dataSource={availableProducts}
                renderItem={product => (
                  <List.Item
                    style={{ cursor: selectedShelf ? 'pointer' : 'not-allowed' }}
                    onClick={() => selectedShelf && handleAddProduct(product, selectedShelf)}
                  >
                    <List.Item.Meta
                      avatar={<Avatar src={product.image} shape="square" />}
                      title={product.name}
                      description={product.code}
                    />
                    {selectedShelf && (
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddProduct(product, selectedShelf);
                        }}
                      />
                    )}
                  </List.Item>
                )}
              />
              {!selectedShelf && (
                <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
                  {t('planogramEditor.selectShelfFirst')}
                </div>
              )}
            </TabPane>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default PlanogramEditor;